import React, { FC, useEffect, useState } from 'react';
import { Box, Dialog, Typography } from '@mui/material';
import logoWhite from '../../assets/logo-white.png';
import SetupGateStep from './SetupGateStep';
import MeasurementStep, { MeasurementSystem } from './MeasurementStep';
import JoinTeamStep, { CODE_LENGTH } from './JoinTeamStep';
import TeamJoinSentStep from './TeamJoinSentStep';
import ConnectAthleteStep from './ConnectAthleteStep';
import RequestSentStep from './RequestSentStep';
import NotificationsStep, { DEFAULT_NOTIF_PREFS, NotifPrefs } from './NotificationsStep';
import CompleteStep from './CompleteStep';
import useService from '../../hooks/service.hook';
import UserService from '../../services/user.service';
import GuardianService from '../../services/guardian.service';
import TeamsService from '../../services/teams.service';
import { User } from '../../models/user.models';
import { isEmailInvalid } from '../../helpers/general.helpers';

const ATHLETE_GRADIENT = 'linear-gradient(135deg, #1565E8 0%, #3D8EFF 100%)';
const GUARDIAN_GRADIENT = 'linear-gradient(135deg, #C95B0A 0%, #F2994A 100%)';
const TOTAL_STEPS = 8;

type ModalScreen =
  | 'gate'
  | 'measurement'
  | 'join-team'
  | 'join-team-sent'
  | 'connect-athlete'
  | 'request-sent'
  | 'notifications'
  | 'complete';

interface LeftPanelConfig {
  headline: string;
  tagline: string;
  step: number;
}

const ATHLETE_CONFIGS: Record<ModalScreen, LeftPanelConfig> = {
  gate: {
    headline: 'Almost there!',
    tagline: 'A few quick preferences to set up your experience — or jump right in.',
    step: 3,
  },
  measurement: {
    headline: 'Units & Measurements',
    tagline: "We'll display all your training data using your preferred measurement system.",
    step: 4,
  },
  'join-team': {
    headline: 'Join Your Team',
    tagline: 'Your coach will give you an 8-character code to connect with your team.',
    step: 5,
  },
  'join-team-sent': {
    headline: 'Join Your Team',
    tagline: 'Your request is on its way to your coach for approval.',
    step: 6,
  },
  'connect-athlete': {
    headline: 'Connect to Your Athlete',
    tagline: "Enter your athlete's email to request guardian access to their existing account.",
    step: 5,
  },
  'request-sent': {
    headline: 'Request Sent!',
    tagline: "Your athlete will be notified. You'll be linked once they approve.",
    step: 6,
  },
  notifications: {
    headline: 'Stay Informed',
    tagline: 'Choose which notifications matter most to you. You can always adjust these later.',
    step: 7,
  },
  complete: {
    headline: 'Welcome to CoeLab!',
    tagline: "Your account is set up and ready to go. Let's get started.",
    step: 8,
  },
};

const GUARDIAN_CONFIGS: Record<ModalScreen, LeftPanelConfig> = {
  gate: {
    headline: 'Almost there!',
    tagline: 'A few quick preferences to set up your experience — or jump right in.',
    step: 3,
  },
  measurement: {
    headline: 'Units & Measurements',
    tagline: "Choose your athlete's preferred measurement system for all training data.",
    step: 4,
  },
  'join-team': {
    headline: 'Join Your Team',
    tagline: 'Your coach will give you an 8-character code to connect with your team.',
    step: 5,
  },
  'join-team-sent': {
    headline: 'Join Your Team',
    tagline: 'Your request is on its way to your coach for approval.',
    step: 6,
  },
  'connect-athlete': {
    headline: 'Connect to Your Athlete',
    tagline: "Enter your athlete's email to request guardian access to their existing account.",
    step: 5,
  },
  'request-sent': {
    headline: 'Request Sent!',
    tagline: "Your athlete will be notified. You'll be linked once they approve.",
    step: 6,
  },
  notifications: {
    headline: 'Stay Informed',
    tagline: "Choose which notifications matter most for monitoring your athlete's training.",
    step: 7,
  },
  complete: {
    headline: 'Welcome to CoeLab!',
    tagline: "You're connected and ready to support your athlete's journey.",
    step: 8,
  },
};

export interface OnboardingModalProps {
  open: boolean;
  accentColor: string;
  onContinue: () => void;
  onSkip: () => void;
}

const OnboardingModal: FC<OnboardingModalProps> = ({ open, accentColor, onContinue, onSkip }) => {
  const isGuardian = accentColor !== '#0D61FF';
  const gradient = isGuardian ? GUARDIAN_GRADIENT : ATHLETE_GRADIENT;
  const configs = isGuardian ? GUARDIAN_CONFIGS : ATHLETE_CONFIGS;

  const [screen, setScreen] = useState<ModalScreen>('gate');

  // ─── Measurement state ────────────────────────────────────────────────────
  const [measurementSystem, setMeasurementSystem] = useState<MeasurementSystem>(() => {
    try {
      const user: User = JSON.parse(window.localStorage.getItem('user') || '{}');
      return (user.measurementSystem as MeasurementSystem) || 'imperial';
    } catch {
      return 'imperial';
    }
  });
  const [isMeasurementLoading, setIsMeasurementLoading] = useState(false);

  // ─── Join team state (athlete only) ──────────────────────────────────────
  const [teamCode, setTeamCode] = useState<string[]>(Array(CODE_LENGTH).fill(''));
  const [teamCodeError, setTeamCodeError] = useState(false);
  const [isTeamLoading, setIsTeamLoading] = useState(false);

  // ─── Connect athlete state (guardian only) ────────────────────────────────
  const [athleteEmail, setAthleteEmail] = useState('');
  const [athleteError, setAthleteError] = useState(false);
  const [isAthleteLoading, setIsAthleteLoading] = useState(false);

  // ─── Notifications state ──────────────────────────────────────────────────
  const [notifPrefs, setNotifPrefs] = useState<NotifPrefs>({ ...DEFAULT_NOTIF_PREFS });
  const [isNotifLoading, setIsNotifLoading] = useState(false);

  const userService = useService(UserService);
  const guardianService = useService(GuardianService);
  const teamsService = useService(TeamsService);

  // Load notification preferences when the modal opens
  useEffect(() => {
    if (!open) return;
    userService
      .getNotificationPreferences()
      .then((response: any) => {
        const data = response.data.data;
        if (data && typeof data === 'object') {
          setNotifPrefs((prev) => ({
            ...prev,
            ...Object.fromEntries(Object.entries(data).map(([k, v]) => [k, Boolean(v)])),
          }));
        }
      })
      .catch(() => {
        // Keep defaults on error
      });
  }, [open]); // eslint-disable-line

  const { headline, tagline, step } = configs[screen];

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const handleMeasurementNext = () => {
    setIsMeasurementLoading(true);
    userService
      .updateMeasurementSystem(measurementSystem)
      .then((response: any) => {
        const updatedSystem = response.data.data.measurementSystem as string;
        try {
          const existingUser: User = JSON.parse(window.localStorage.getItem('user') as string);
          existingUser.measurementSystem = updatedSystem;
          window.localStorage.setItem('user', JSON.stringify(existingUser));
        } catch {
          // localStorage unavailable
        }
        setIsMeasurementLoading(false);
        setScreen(isGuardian ? 'connect-athlete' : 'join-team');
      })
      .catch(() => {
        setIsMeasurementLoading(false);
        setScreen(isGuardian ? 'connect-athlete' : 'join-team');
      });
  };

  const handleJoinTeam = () => {
    setTeamCodeError(false);
    setIsTeamLoading(true);
    teamsService
      .postTeamJoinRequest({ inviteCode: teamCode.join(''), requestType: 'athlete' })
      .then(() => {
        setIsTeamLoading(false);
        setScreen('join-team-sent');
      })
      .catch(() => {
        setIsTeamLoading(false);
        setTeamCodeError(true);
      });
  };

  const handleRequestGuardianAccess = () => {
    if (isEmailInvalid(athleteEmail)) {
      setAthleteError(true);
      return;
    }
    setIsAthleteLoading(true);
    guardianService
      .inviteGuardian(athleteEmail)
      .then(() => {
        setIsAthleteLoading(false);
        setScreen('request-sent');
      })
      .catch(() => {
        setIsAthleteLoading(false);
        setAthleteError(true);
      });
  };

  const handleEnableNotifications = () => {
    setIsNotifLoading(true);
    userService
      .setNotificationPreferences(notifPrefs as any)
      .then(() => {
        setIsNotifLoading(false);
        setScreen('complete');
      })
      .catch(() => {
        setIsNotifLoading(false);
        setScreen('complete');
      });
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  const centeredScreens: ModalScreen[] = ['gate', 'measurement', 'join-team-sent', 'request-sent', 'complete'];
  const rightPanelCentered = centeredScreens.includes(screen);

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          minHeight: 480,
        },
      }}
      // Prevent closing by clicking backdrop — user must choose Continue or Skip
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      onClose={() => {}}
    >
      <Box sx={{ display: 'flex', minHeight: 480 }}>
        {/* ── Left branded panel ── */}
        <Box
          sx={{
            display: { xs: 'none', sm: 'flex' },
            flexDirection: 'column',
            justifyContent: 'space-between',
            width: '42%',
            flexShrink: 0,
            background: gradient,
            p: 4,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Decorative circles */}
          <Box
            sx={{
              position: 'absolute',
              top: -80,
              right: -80,
              width: 260,
              height: 260,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.08)',
              pointerEvents: 'none',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: -60,
              left: -60,
              width: 200,
              height: 200,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.06)',
              pointerEvents: 'none',
            }}
          />

          {/* Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, position: 'relative', zIndex: 1 }}>
            <img src={logoWhite} alt="CoeLab" style={{ height: 26 }} />
          </Box>

          {/* Headline + tagline */}
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700, mb: 1.5, lineHeight: 1.25 }}>
              {headline}
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.82)', fontSize: 13, lineHeight: 1.6 }}>{tagline}</Typography>
          </Box>

          {/* Step dots */}
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', gap: 0.75, mb: 0.75 }}>
              {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
                <Box
                  key={i}
                  sx={{
                    width: i === step - 1 ? 20 : 8,
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: i === step - 1 ? '#fff' : 'rgba(255,255,255,0.35)',
                    transition: 'width 0.2s ease',
                  }}
                />
              ))}
            </Box>
            <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 12 }}>
              Step {step} of {TOTAL_STEPS}
            </Typography>
          </Box>
        </Box>

        {/* ── Right content panel ── */}
        <Box
          sx={{
            flex: 1,
            backgroundColor: '#fff',
            display: 'flex',
            alignItems: rightPanelCentered ? 'center' : 'flex-start',
            px: { xs: 3, sm: 5 },
            py: 4,
            overflowY: 'auto',
          }}
        >
          <Box sx={{ width: '100%' }}>
            {screen === 'gate' && (
              <SetupGateStep accentColor={accentColor} onContinue={() => setScreen('measurement')} onSkip={onSkip} />
            )}
            {screen === 'measurement' && (
              <MeasurementStep
                accentColor={accentColor}
                isGuardian={isGuardian}
                measurementSystem={measurementSystem}
                isLoading={isMeasurementLoading}
                onMeasurementChange={setMeasurementSystem}
                onBack={() => setScreen('gate')}
                onNext={handleMeasurementNext}
              />
            )}
            {screen === 'join-team' && (
              <JoinTeamStep
                accentColor={accentColor}
                teamCode={teamCode}
                hasError={teamCodeError}
                isLoading={isTeamLoading}
                onChange={(idx, val) => {
                  setTeamCodeError(false);
                  setTeamCode((prev) => {
                    const next = [...prev];
                    next[idx] = val;
                    return next;
                  });
                }}
                onJoin={handleJoinTeam}
                onSkip={() => setScreen('notifications')}
                onBack={() => setScreen('measurement')}
              />
            )}
            {screen === 'join-team-sent' && (
              <TeamJoinSentStep accentColor={accentColor} onContinue={() => setScreen('notifications')} />
            )}
            {screen === 'connect-athlete' && (
              <ConnectAthleteStep
                athleteEmail={athleteEmail}
                hasError={athleteError}
                isLoading={isAthleteLoading}
                onEmailChange={(v) => {
                  setAthleteError(false);
                  setAthleteEmail(v);
                }}
                onRequest={handleRequestGuardianAccess}
                onTryAgain={() => {
                  setAthleteError(false);
                  handleRequestGuardianAccess();
                }}
                onSkip={() => setScreen('notifications')}
                onBack={() => setScreen('measurement')}
              />
            )}
            {screen === 'request-sent' && <RequestSentStep onContinue={() => setScreen('notifications')} />}
            {screen === 'notifications' && (
              <NotificationsStep
                accentColor={accentColor}
                isGuardian={isGuardian}
                preferences={notifPrefs}
                isLoading={isNotifLoading}
                onChange={(key, value) => setNotifPrefs((prev) => ({ ...prev, [key]: value }))}
                onBack={() => setScreen(isGuardian ? 'request-sent' : 'join-team')}
                onEnable={handleEnableNotifications}
                onSkip={() => setScreen('complete')}
              />
            )}
            {screen === 'complete' && (
              <CompleteStep accentColor={accentColor} isGuardian={isGuardian} onComplete={onContinue} />
            )}
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default OnboardingModal;
